import { observable, runInAction } from "mobx"

import { getLogger } from "@/lib/logging.ts"

import type { GrazieLLMProfileDTO } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Persistence } from "@/lib/persistence.ts"
import type { Queries } from "@/lib/queries.ts"
import type { IObservableArray, IObservableValue } from "mobx"


const log = getLogger("LLMSelection")

const LLM_ID_STORAGE_KEY = "last-used-llm-id"
const LLM_ID_STORAGE_VERSION = 2 // Remember to increment this when changing PREFERRED_LLM_ID
const PREFERRED_LLM_ID = "anthropic-claude-4-5-sonnet"

export class LLMSelection {
  constructor(readonly persistence: Persistence, availableModels: GrazieLLMProfileDTO[], selectedLlmId: string) {
    this.availableModels = observable(availableModels)
    this.selectedLlmId = observable.box(selectedLlmId)
  }

  readonly availableModels: IObservableArray<GrazieLLMProfileDTO>
  readonly selectedLlmId: IObservableValue<string>

  setSelectedLlmId = (value: string) => {
    runInAction(() => {
      this.persistence.setItemWithVersion(LLM_ID_STORAGE_KEY, value, LLM_ID_STORAGE_VERSION)
      this.selectedLlmId.set(value)
    })
  }
}

export async function createLLMSelection(queries: Queries, persistence: Persistence): Promise<LLMSelection | null> {
  const availableLlmIds = await queries.getLlmProfileIds()
  const lastUsedLlmId = persistence.getItemWithVersion(LLM_ID_STORAGE_KEY, LLM_ID_STORAGE_VERSION)
  let selectedLlm: GrazieLLMProfileDTO | null = availableLlmIds.find(it => it.id === lastUsedLlmId) ?? null

  if (!selectedLlm) {
    selectedLlm = availableLlmIds.find(it => it.id === PREFERRED_LLM_ID) ?? null
  }

  if (!selectedLlm && availableLlmIds.length > 0) {
    selectedLlm = availableLlmIds[0]
  }

  if (!selectedLlm) {
    log.error("No LLM profiles available")
    return null
  }

  return new LLMSelection(persistence, availableLlmIds, selectedLlm!.id)
}